import { SEO } from '@quadralize/ui'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'
import { useAppStore } from '@/stores/use-app-store'
import { Header } from '../components/header/header'
import { MobileNav } from '../components/mobile-nav/mobile-nav'
import { Sidebar } from '../components/sidebar/sidebar'

const SIDEBAR_THRESHOLD = 1024

export function BackofficeLayout() {
  const { t } = useTranslation('layout')
  const { isSidebarOpen, setSidebarOpen, setSidebarCollapsed } = useAppStore()
  const prevWidthRef = useRef(window.innerWidth)
  const contentScrollRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth

      if (prevWidthRef.current >= SIDEBAR_THRESHOLD && width < SIDEBAR_THRESHOLD) {
        setSidebarCollapsed(true)
      }

      if (prevWidthRef.current < SIDEBAR_THRESHOLD && width >= SIDEBAR_THRESHOLD) {
        setSidebarCollapsed(false)
      }

      prevWidthRef.current = width
    }

    setSidebarCollapsed(window.innerWidth < SIDEBAR_THRESHOLD)

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [setSidebarCollapsed])

  useEffect(() => {
    if (!location.key) {
      return
    }

    contentScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.key])

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-bg-base">
      <SEO
        title={t('shell.title')}
        description={t('shell.description')}
        themeColor="#09090b"
        keywords={['backoffice', 'administração', 'quadras', 'gestão', 'operacional']}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: t('shell.title'),
          applicationCategory: 'BusinessApplication',
        }}
      />

      <Sidebar className="hidden md:flex" />

      <div className="flex flex-1 min-h-0 flex-col overflow-hidden min-w-0 pb-16 md:pb-0">
        <Header />

        <div
          ref={contentScrollRef}
          className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto overscroll-y-contain"
        >
          <main className="h-full min-h-0 p-4 md:p-8">
            <div className="mx-auto h-full min-h-0 w-full">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      <MobileNav />

      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 z-50 bg-black/50 md:hidden backdrop-blur-sm"
            />

            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 z-50 md:hidden shadow-2xl"
            >
              <Sidebar isMobile onClose={() => setSidebarOpen(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
